import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Observable, map, of, tap } from 'rxjs';

import { OperatorSettingsApi } from '../api/operator-settings.api';
import { PSIDUploadResponseDto } from '../api/dtos/operator-settings.dto';
import { UpdateCurrencyResponseDto } from '../api/dtos/currency.dto';
import { OperatorSettings } from '../models/operator-settings.model';
import { Currency, CurrencyUpdatePayload } from '../models/currency.model';

@Injectable({ providedIn: 'root' })
export class OperatorSettingsService {
  private readonly _api = inject(OperatorSettingsApi);
  private readonly _document = inject(DOCUMENT);

  public getAll(): Observable<OperatorSettings[]> {
    return this._api.getAll();
  }

  public update(
    id: number,
    data: Partial<OperatorSettings>,
  ): Observable<OperatorSettings[]> {
    return this._api.update(id, data);
  }

  public uploadPSIDs(formData: FormData): Observable<PSIDUploadResponseDto> {
    return this._api.uploadPSIDs(formData);
  }

  public uploadUnicornQuotas(file: File): Observable<unknown> {
    return this._api.uploadUnicornQuotas(file);
  }

  public downloadUnicornMappings(): Observable<void> {
    return this._api.downloadUnicornMappings().pipe(
      tap((res) => this._saveBlob(res, 'unicorn-quotas.xlsx')),
      map(() => undefined),
    );
  }

  public uploadCrmIdFile(file: File): Observable<unknown> {
    return this._api.uploadCrmIdFile(file);
  }

  public downloadUnicornTemplate(): Observable<void> {
    return of(undefined).pipe(
      tap(() =>
        this._document.defaultView?.open(
          '/templates/unicorn-quotas-template.xlsx',
          '_blank',
          'noopener,noreferrer',
        ),
      ),
    );
  }

  public downloadCrmIdTemplate(): Observable<void> {
    return of(undefined).pipe(
      tap(() =>
        this._document.defaultView?.open(
          '/templates/crm-id-template.csv',
          '_blank',
          'noopener,noreferrer',
        ),
      ),
    );
  }

  public getAllCurrencies(): Observable<Currency[]> {
    return this._api.getAllCurrencies();
  }

  public updateCurrency(
    currencyShortCode: string,
    data: CurrencyUpdatePayload,
  ): Observable<UpdateCurrencyResponseDto> {
    return this._api.updateCurrency(currencyShortCode, data);
  }

  public uploadTrafficDensityFile(
    file: File,
    country: string,
  ): Observable<unknown> {
    return this._api.uploadTrafficDensityFile(file, country);
  }

  public downloadTrafficDensityTemplate(): Observable<void> {
    return this._api.downloadTrafficDensityTemplate().pipe(
      tap((res) => this._saveBlob(res, 'traffic-density-file-template')),
      map(() => undefined),
    );
  }

  public downloadTrafficDensityCurrentFile(country: string): Observable<void> {
    return this._api.downloadTrafficDensityCurrentFile(country).pipe(
      tap((res) => this._saveBlob(res, `${country}-traffic-density-file`)),
      map(() => undefined),
    );
  }

  private _saveBlob(
    response: HttpResponse<Blob>,
    fallbackFileName: string,
  ): void {
    const contentDisposition =
      response.headers.get('content-disposition') ?? '';
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    const fileName = match?.[1]?.trim() || fallbackFileName;
    const url = URL.createObjectURL(response.body as Blob);
    const downloadLink = this._document.createElement('a');
    downloadLink.href = url;
    downloadLink.download = fileName;
    downloadLink.click();
    URL.revokeObjectURL(url);
  }
}
